import Vue from 'vue'
import Router from 'vue-router'
import AppState from '@/plugins/appstate'
import Logger from '@/plugins/logger'
import { session, store } from '@/api/store'
import { auth, calcLicense, getUserData, SetHeader, setPermission, user } from '@/api/access'
import { LangFrame, MainFrame } from '@/components/frame'
import {
  EmailInvitationComponent,
  ForgotComponent,
  JoinComponent,
  JoinDoneComponent,
  LoginComponent,
  ResetPwComponent,
  WelcomeComponent,
} from '@/components/access'
import { DeviceComponent, DeviceDetailComponent } from '@/components/device'
import {
  LogAccessComponent,
  LogDeleteComponent,
  LogRemoteComponent,
  LogTransferComponent,
  LogWarningComponent,
  RateRemoteComponent,
  RateSummaryComponent,
  RateWarningComponent,
  StatisticsComponent,
} from '@/components/statistics'
import {
  AccessRegistrationComponent,
  AccessRegistrationViewComponent,
  AccessUserComponent,
  AddManagerComponent,
  BlockingSettingsComponent,
  EditManagerComponent,
  ExcelAddManagerComponent,
  FunctionSettingsComponent,
  GroupsComponent,
  GroupsDetailComponent,
  GroupsListTableComponent,
  LicenseComponent,
  ManageComponent,
  ManagerListComponent,
  ManagersComponent,
  PaymentsComponent,
  ProfileComponent,
} from '@/components/manage'
import { DownloadComponent, NoticeComponent, NoticeDetailComponent, SupportComponent } from '@/components/support'
import { TicketComponent, TicketRequestComponent } from '@/components/ticket'
import { ErrorComponent } from '@/components/error'
import { getCurrentLanguage, setI18nLanguage } from '@/i18n'

Vue.use(Router)

const checkAuth = () => {
  if (auth()) return true
  else {
    const stored = store.get('user')
    const token = session.get('token')

    if (!token) return false
    if (stored) {
      SetHeader('token', stored.token)
      auth(true)
      user(stored)
      if (!AppState.Info.dateFmt) {
        AppState.Info.dateFmt = stored.dateFmt
      }
      calcLicense(stored.pay)
      setPermission(stored)
      return true
    } else {
      return false
    }
  }
}

const alreadyAuth = (to, from, next) => {
  const lang = store.get('lang') || 'en'
  return checkAuth() ? next({name: 'deviceAll', params: {groupPk: 'all', lang}}) : next()
}

const commonGuard = (to, from, next) => {
  return checkAuth() ? next() : next('/')
}

const licenseGuard = (to, from, next) => {
  if (checkAuth()) {
    if (AppState.isCorporate) next()
    else next({name: 'error', params: {code: '404'}})
  } else {
    next('/')
  }
}

const adminGuard = (to, from, next) => {
  if (checkAuth()) {
    if (AppState.perm.isAdmin) next()
    else next({name: 'error', params: {code: '404'}})
  } else {
    next('/')
  }
}

const router = new Router({
  routes: [
    {
      path: '/',
      redirect: { name: 'login', params: { lang: getCurrentLanguage() } }
    },
    {
      path: '/access/auth_token',
      beforeEnter (to, from, next) {
        let {token, redirect} = to.query
        if (!token) return next({name: 'login', params: {lang: 'ko'}})
        const skipHistoryFlag = true
        getUserData(token, (err, userData) => {
          if (err) return next('error')
          if (!redirect) redirect = {name: 'license', params: {lang: 'ko'}}
          next(redirect)
        }, skipHistoryFlag)
      },
      component: {
        template: '<div>- PROCESSING...</div>'
      }
    },
    {
      path: '/:lang',
      component: LangFrame,
      children: [
        {
          path: '',
          name: 'login',
          beforeEnter: alreadyAuth,
          component: LoginComponent
        },
        {
          path: 'join-us',
          name: 'join-us',
          component: JoinComponent
        },
        {
          path: 'forgot',
          name: 'forgot',
          component: ForgotComponent
        },
        {
          path: 'forgot/:type',
          name: 'forgot',
          component: ForgotComponent
        },
        {
          path: 'access/auth',
          name: 'access-auth',
          component: WelcomeComponent
        },
        {
          path: 'access/retypepassword',
          name: 'access-retypepassword',
          component: ResetPwComponent
        },
        {
          path: 'join-done',
          name: 'join-done',
          component: JoinDoneComponent
        },
        {
          path: 'email-invitation',
          name: 'email-invitation',
          component: EmailInvitationComponent
        },
        {
          path: 'welcome',
          name: 'welcome',
          component: WelcomeComponent
        },
        {
          path: 'device',
          name: 'device-root',
          redirect: {name: 'deviceAll', params: {groupPk: 'all', lang: getCurrentLanguage()}},
          component: MainFrame,
          children: [
            {
              path: ':groupPk',
              name: 'deviceAll',
              beforeEnter: commonGuard,
              component: DeviceComponent,
              children: [
                {
                  path: ':devicePk',
                  name: 'device-detail',
                  component: DeviceDetailComponent,
                  beforeEnter: commonGuard,
                  props: true
                }
              ]
            }
          ]
        },
        {
          path: 'application',
          component: () => import("@/components/frame/main.vue"),
          children: [
            {
              path: '',
              name: 'application-root',
              beforeEnter: commonGuard,
              redirect: {name: 'applicationAll', params: {groupPk: 'all', lang: getCurrentLanguage()}},
              component: () => import("@/components/application/ApplicationIndex.vue"),
              children: [
                {
                  path: ':groupPk',
                  name: 'applicationAll',
                  component: () => import("@/components/application/ApplicationMain.vue"),
                  props: true,
                },
                {
                  path: ":groupPk/:applicationPk",
                  name: "application-detail",
                  component: () => import("@/components/application/ApplicationDetail.vue"),
                  props: true,
                }
              ],
            }
          ]
        },
        {
          path: 'appconfig',
          component: () => import("@/components/frame/main.vue"),
          children: [
            {
              path: '',
              name: 'app-config-root',
              beforeEnter: commonGuard,
              redirect: {name: 'appconfig', params: {lang: getCurrentLanguage()}},
              component: () => import("@/components/appConfig/AppConfigIndex.vue"),
              children: [
                {
                  path: '',
                  name: 'appconfig',
                  component: () => import("@/components/appConfig/AppConfigMain.vue"),
                  props: true,
                },
              ],
            }
          ]
        },
        {
          path: 'access-ticket',
          redirect: {name: 'ticket-request'},
          component: MainFrame,
          children: [
            {
              path: '',
              redirect: {name: 'ticket-request'},
              component: TicketComponent,
              children: [
                {
                  path: 'request',
                  name: 'ticket-request',
                  component: TicketRequestComponent,
                  beforeEnter: commonGuard
                }
              ]
            }
          ]
        },
        {
          path: 'statistics',
          component: MainFrame,
          children: [
            {
              path: '',
              name: 'statistics',
              beforeEnter: licenseGuard,
              redirect: { name: 'rate-summary' },
              component: StatisticsComponent,
              children: [
                {
                  path: 'rate/summary',
                  name: 'rate-summary',
                  component: RateSummaryComponent,
                  beforeEnter: licenseGuard
                },
                {
                  path: 'rate/remote',
                  name: 'rate-remote',
                  component: RateRemoteComponent,
                  beforeEnter: licenseGuard
                },
                {
                  path: 'rate/warning',
                  name: 'rate-warning',
                  component: RateWarningComponent,
                  beforeEnter: licenseGuard
                },
                {
                  path: 'log/remote',
                  name: 'log-remote',
                  component: LogRemoteComponent,
                  beforeEnter: licenseGuard
                },
                {
                  path: 'log/warning',
                  name: 'log-warning',
                  component: LogWarningComponent,
                  beforeEnter: licenseGuard
                },
                {
                  path: 'log/access',
                  name: 'log-access',
                  component: LogAccessComponent,
                  beforeEnter: licenseGuard
                },
                {
                  path: 'log/transfer',
                  name: 'log-transfer',
                  component: LogTransferComponent,
                  beforeEnter: licenseGuard
                },
                {
                  path: 'log/delete',
                  name: 'log-delete',
                  component: LogDeleteComponent,
                  beforeEnter: licenseGuard
                }
              ]
            }
          ]
        },
        {
          path: 'management',
          component: MainFrame,
          children: [
            {
              path: '',
              name: 'management',
              beforeEnter: commonGuard,
              redirect: { name: 'profile' },
              component: ManageComponent,
              children: [
                {
                  path: 'profile',
                  name: 'profile',
                  component: ProfileComponent,
                  beforeEnter: commonGuard
                },
                {
                  path: 'managers',
                  redirect: { name: 'managers' },
                  component: ManagersComponent,
                  children: [
                    {
                      path: '',
                      name: 'managers',
                      component: ManagerListComponent,
                      beforeEnter: adminGuard
                    },
                    {
                      path: 'add',
                      name: 'add-manager',
                      component: AddManagerComponent,
                      beforeEnter: adminGuard
                    },
                    {
                      path: ':userPk',
                      name: 'edit-manager',
                      component: EditManagerComponent,
                      beforeEnter: adminGuard,
                      props: true
                    },
                    {
                      path: 'excel',
                      name: 'excel-add-manager-component',
                      component: ExcelAddManagerComponent,
                      beforeEnter: adminGuard
                    }
                  ]
                },
                {
                  path: 'groups',
                  name: 'group-root',
                  redirect: { name: 'groups' },
                  component: GroupsComponent,
                  beforeEnter: adminGuard,
                  children: [
                    {
                      path: '',
                      name: 'groups',
                      beforeEnter: adminGuard,
                      // component: GroupsListComponent
                      component: GroupsListTableComponent
                    },
                    {
                      path: 'detail',
                      redirect: { name: 'groups' },
                      component: {template: '<router-view></router-view>'},
                      beforeEnter: adminGuard,
                      children: [
                        {
                          path: ':groupPk',
                          name: 'group-detail',
                          beforeEnter: adminGuard,
                          component: GroupsDetailComponent,
                          props: true
                        }
                      ]
                    },
                    {
                      path: ':groupPk',
                      name: 'groups-sub',
                      beforeEnter: adminGuard,
                      // component: GroupsListComponent,
                      component: GroupsListTableComponent,
                      props: true
                    }
                  ]
                },
                {
                  path: 'license',
                  name: 'license',
                  component: LicenseComponent,
                  beforeEnter: adminGuard
                },
                {
                  path: 'payments',
                  name: 'payments',
                  component: PaymentsComponent,
                  beforeEnter: adminGuard
                },
                {
                  path: 'access',
                  name: 'access-registration',
                  component: AccessRegistrationComponent,
                  beforeEnter: commonGuard
                },
                {
                  path: 'access-user',
                  name: 'access-user',
                  component: AccessUserComponent,
                  beforeEnter: commonGuard
                },
                {
                  path: 'access-history',
                  name: 'access-registration-view',
                  component: AccessRegistrationViewComponent,
                  beforeEnter: commonGuard
                },
                {
                  path: 'function-settings',
                  name: 'function-settings',
                  component: FunctionSettingsComponent,
                  beforeEnter: commonGuard
                },
                {
                  path: 'blocking-settings',
                  name: 'blocking-settings',
                  component: BlockingSettingsComponent,
                  beforeEnter: commonGuard
                }
              ]
            }
          ]
        },
        {
          path: 'support',
          component: MainFrame,
          children: [
            {
              path: '',
              name: 'support',
              redirect: { name: 'downloads' },
              component: SupportComponent,
              beforeEnter: commonGuard,
              children: [
                {
                  path: 'downloads',
                  name: 'downloads',
                  component: DownloadComponent,
                  beforeEnter: commonGuard
                },
                {
                  path: 'notice',
                  name: 'notice',
                  component: NoticeComponent,
                  beforeEnter: commonGuard
                },
                {
                  path: 'notice/:pk',
                  name: 'notice-detail',
                  component: NoticeDetailComponent,
                  beforeEnter: commonGuard,
                  props: true
                }
              ]
            }
          ]
        },
        {
          path: 'error/:code',
          name: 'error',
          component: ErrorComponent,
          props: true
        },
        { path: '*', component: ErrorComponent, props: { code: 404 } }
      ]
    }
  ]
})

router.beforeEach((to, from, next) => {
  if (to.path === '/access/auth_token') {
    return next()
  }
  const {lang} = to.params
  if (!lang || !/ko|en|ja/.test(lang)) {
    next('/ko/')
  } else {
    let current = getCurrentLanguage()
    if (current !== lang) {
      Logger.info(`LANG CHANGED: ${lang}`)
      store.set('lang', lang)
      setI18nLanguage(lang)
      next()
      setTimeout(() => window.location.reload(), 300)
      // loadLanguageAsync(lang).then(() => next())
    } else {
      next()
    }
  }
})

export default router